// Watches for anything during a test that makes its results untrustworthy: leaving
// fullscreen, hiding the page, resizing or moving the window to another screen, or a change
// of colour depth. Critical events stop the test; the server marks it invalid.

export type Severity = "critical" | "warning";

export interface ComplianceEvent {
  t_ms: number;
  type: string;
  severity: Severity;
  detail?: string;
}

export interface ComplianceReport {
  events: ComplianceEvent[];
  watched: string[];
}

interface ScreenDetailsLike extends EventTarget {
  currentScreen?: { label?: string };
}

export class ComplianceMonitor {
  readonly events: ComplianceEvent[] = [];
  private readonly t0 = performance.now();
  private readonly cleanups: Array<() => void> = [];
  private readonly startW = window.innerWidth;
  private readonly startH = window.innerHeight;
  private readonly startDpr = window.devicePixelRatio;
  private readonly startDepth = window.screen.colorDepth;
  private readonly startFullscreen = !!document.fullscreenElement;
  readonly watched: string[] = [];

  constructor(private readonly onCritical: (e: ComplianceEvent) => void,
              opts: { requireFocus?: boolean; requireFullscreen?: boolean; screenDetails?: ScreenDetailsLike | null } = {}) {
    const add = (target: EventTarget, type: string, fn: () => void) => {
      target.addEventListener(type, fn);
      this.cleanups.push(() => target.removeEventListener(type, fn));
    };
    if (this.startFullscreen) {
      this.watched.push("fullscreen");
      add(document, "fullscreenchange", () => {
        if (!document.fullscreenElement) this.report("left_fullscreen", "critical");
      });
    }
    this.watched.push("visibility");
    add(document, "visibilitychange", () => {
      if (document.visibilityState === "hidden") this.report("page_hidden", "critical");
    });
    this.watched.push("focus");
    add(window, "blur", () => this.report("lost_focus", opts.requireFocus ? "critical" : "warning"));
    this.watched.push("window_size");
    add(window, "resize", () => {
      if (Math.abs(window.innerWidth - this.startW) > 2 || Math.abs(window.innerHeight - this.startH) > 2) {
        this.report("window_resized", "critical", `${this.startW}x${this.startH} -> ${window.innerWidth}x${window.innerHeight}`);
      }
      if (window.devicePixelRatio !== this.startDpr) {
        this.report("pixel_ratio_changed", "critical", `${this.startDpr} -> ${window.devicePixelRatio}`);
      }
      if (window.screen.colorDepth !== this.startDepth) {
        this.report("color_depth_changed", "critical", `${this.startDepth} -> ${window.screen.colorDepth}`);
      }
    });
    if (opts.requireFullscreen && !this.startFullscreen) {
      // report after construction so the caller's handler sees a fully built monitor
      queueMicrotask(() => this.report("not_fullscreen", "critical"));
    }
    const sd = opts.screenDetails;
    if (sd) {
      this.watched.push("screen");
      add(sd, "currentscreenchange", () => this.report("moved_to_another_screen", "critical", sd.currentScreen?.label));
    }
  }

  report(type: string, severity: Severity, detail?: string): void {
    const e: ComplianceEvent = { t_ms: Math.round(performance.now() - this.t0), type, severity, detail };
    // collapse repeats of the same event within 250 ms
    const last = this.events[this.events.length - 1];
    if (last && last.type === type && e.t_ms - last.t_ms < 250) return;
    this.events.push(e);
    if (severity === "critical") this.onCritical(e);
  }

  get criticalCount(): number {
    return this.events.filter((e) => e.severity === "critical").length;
  }

  stop(): ComplianceReport {
    this.cleanups.splice(0).forEach((f) => f());
    return { events: [...this.events], watched: [...this.watched] };
  }
}
